import { Flex, Spacer, Spinner, Text, VisuallyHidden } from '@zillow/constellation';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import React, { useEffect, useState } from 'react';
import numberUtils from 'app/shared/utils/numberUtils';
import { capitalizeFirstLetter } from 'app/shared/utils/stringUtils';
import DataCard from 'app/shared/components/DataCard';
import LeftAlignedYAxisTick from 'app/shared/components/LeftAlignedYAxisTick';
import ChartTooltip from 'app/shared/components/ChartTooltip';
import Dot from './Dot';

import type { HistoricRentPrice } from 'app/shared/types/HistoricRentPrice';

interface RentLineGraphProps {
    curYear: Array<HistoricRentPrice>;
    prevYear: Array<HistoricRentPrice>;
    yearlyChange: number | null;
    isSmallScreen: boolean;
}

const BLUE_500 = '#0D4599';
const AQUA_400 = '#059EC2';

const RentLineGraph: React.FunctionComponent<RentLineGraphProps> = (props) => {
    const { curYear, prevYear, yearlyChange, isSmallScreen } = props;

    const [isClientLoaded, setIsClientLoaded] = useState(false);
    useEffect(() => {
        setIsClientLoaded(true);
    }, []);

    const data = prevYear.map((medRent, i) => {
        return {
            month: capitalizeFirstLetter(medRent.month),
            prevPrice: medRent.price || null,
            ...(i < curYear.length && { price: curYear[i].price || null })
        };
    });

    const isIncomplete = {
        curYear: curYear.some((rentPrice: HistoricRentPrice) => rentPrice.price === 0),
        prevYear: prevYear.some((rentPrice: HistoricRentPrice) => rentPrice.price === 0)
    };

    const thisYearLabel = curYear.length ? `This year (${curYear[0].year})` : null;
    const lastYearLabel = prevYear.length ? `Last year (${prevYear[0].year})` : null;

    const AccessibleTable = (
        <VisuallyHidden>
            <table>
                <caption>Median rental price over time</caption>
                <thead>
                    <tr>
                        <td></td>
                        <th scope="col">{lastYearLabel}</th>
                        <th scope="col">{thisYearLabel}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.map((medRent) => {
                        const { month, price, prevPrice } = medRent;
                        return (
                            <tr key={month}>
                                <th scope="row">{month}</th>
                                <td>{prevPrice ? numberUtils.asCurrency(prevPrice) : 'No data'}</td>
                                <td>{price ? numberUtils.asCurrency(price) : 'No data'}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </VisuallyHidden>
    );

    return (
        <DataCard
            heading="Median Rental Price over Time"
            copyText={
                yearlyChange ? (
                    <>
                        In the last year, rent has {yearlyChange > 0 ? 'increased' : 'decreased'} by{' '}
                        <strong>{numberUtils.asCurrency(Math.abs(yearlyChange))}</strong> compared to the previous year.
                    </>
                ) : (
                    ''
                )
            }
            useFullScreen={isSmallScreen}
        >
            {isClientLoaded ? (
                <>
                    <Spacer aria-hidden marginTop="sm" marginBottom="xs">
                        <ResponsiveContainer width="100%" height={200}>
                            <LineChart data={data}>
                                <XAxis dataKey="month" axisLine={false} tickLine={false} style={{ fontSize: '14px' }} />
                                <YAxis
                                    axisLine={false}
                                    tickLine={false}
                                    tick={LeftAlignedYAxisTick}
                                    tickFormatter={(value: number) => numberUtils.asCurrency(value)}
                                    style={{ fontSize: '14px' }}
                                    domain={['auto', 'auto']}
                                />
                                <Tooltip
                                    filterNull={false}
                                    content={ChartTooltip}
                                    formatter={(props: any) => {
                                        const year = props.name;
                                        const isFutureDate = props.value === undefined;
                                        return isFutureDate ? null : (
                                            <>
                                                <Text
                                                    fontType="finePrint"
                                                    marginRight="xs"
                                                    style={{ color: props.color }}
                                                >
                                                    ▬
                                                </Text>
                                                <Text fontType="finePrint" marginRight="xs">
                                                    {props.payload.month} {year}
                                                </Text>
                                                <strong>
                                                    {props.value ? numberUtils.asCurrency(props.value) : 'No data'}
                                                </strong>
                                            </>
                                        );
                                    }}
                                    wrapperStyle={{ fontSize: '14px' }}
                                />
                                <CartesianGrid vertical={false} stroke="#F1F1F4" />
                                {prevYear.length && (
                                    <Line
                                        type="monotone"
                                        dataKey="prevPrice"
                                        stroke={AQUA_400}
                                        name={prevYear[0].year}
                                        strokeWidth="3"
                                        dot={isIncomplete.prevYear ? { fill: AQUA_400 } : false}
                                        connectNulls={isIncomplete.prevYear && true}
                                        isAnimationActive={false}
                                    />
                                )}
                                {curYear.length && (
                                    <Line
                                        type="monotone"
                                        dataKey="price"
                                        name={curYear[0].year}
                                        stroke={BLUE_500}
                                        strokeWidth="3"
                                        dot={
                                            isIncomplete.curYear
                                                ? { fill: BLUE_500 }
                                                : (props: any) => <Dot {...props} lastIdx={curYear.length - 1} />
                                        }
                                        connectNulls={isIncomplete.curYear && true}
                                        isAnimationActive={false}
                                    />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </Spacer>
                    {/* recharts Legend component is too buggy to use https://github.com/recharts/recharts/issues/2704 */}
                    <Flex display="flex" flexDirection="row" marginLeft="xl">
                        {thisYearLabel ? (
                            <Text as="p" fontType="bodySmall" aria-hidden>
                                <Text fontColor="blue500" marginRight="xs">
                                    ▬
                                </Text>
                                {thisYearLabel}
                            </Text>
                        ) : null}
                        {lastYearLabel ? (
                            <Text as="p" fontType="bodySmall" marginLeft="sm" aria-hidden>
                                <Text fontColor="aqua400" marginRight="xs">
                                    ▬
                                </Text>
                                {lastYearLabel}
                            </Text>
                        ) : null}
                    </Flex>
                </>
            ) : (
                <Flex display="flex" alignItems="center" justifyContent="center" flex="1">
                    <Spinner size="sm" style={{ height: 250 }} />
                </Flex>
            )}
            {AccessibleTable}
        </DataCard>
    );
};

export default RentLineGraph;
